<template>
  <el-dialog
    custom-class="program-template-install-dialog"
    :visible="value"
    :close-on-click-modal="false"
    :show-close="false"
    width="760px"
    top="8vh"
    @update:visible="(v) => $emit('input', v)"
    @open="resetForm"
  >
    <div class="pti">
      <div class="pti-head">
        <div class="pti-head-left">
          <button type="button" class="pti-close-btn" @click="$emit('input', false)" aria-label="Close">
            <i class="el-icon-close"/>
          </button>
          <div>
            <div class="pti-eyebrow">Install template</div>
            <h2>{{ templateTitle }}</h2>
          </div>
        </div>
        <div v-if="preview" class="pti-counts">
          <span v-if="preview.createCount"><strong>{{ preview.createCount }}</strong> create</span>
          <span v-if="preview.reuseCount"><strong>{{ preview.reuseCount }}</strong> reuse</span>
        </div>
      </div>

      <div class="pti-form">
        <label>
          <span class="pti-label">Program title</span>
          <el-input v-model="form.title" />
        </label>
        <label>
          <span class="pti-label">Description</span>
          <el-input v-model="form.description" type="textarea" :rows="2" />
        </label>
      </div>

      <div v-if="previewError" class="pti-preview-warning" role="status">
        <i class="el-icon-warning-outline"/>
        <span>{{ previewError }}</span>
      </div>

      <div class="pti-sequence">
        <div class="pti-sequence-head">
          <span>Template content</span>
          <span>{{ rows.length }} item{{ rows.length === 1 ? '' : 's' }}</span>
        </div>
        <div v-if="loading" class="pti-loading">
          <i class="el-icon-loading" /> Checking company library...
        </div>
        <div v-else class="pti-rows">
          <div v-for="row in rows" :key="row.key" class="pti-row">
            <div class="pti-row-day">{{ dayLabel(row.offsetDays) }}</div>
            <div class="pti-row-main">
              <div class="pti-row-title">{{ row.title || typeLabel(row.type) }}</div>
              <div class="pti-row-meta">{{ typeLabel(row.type) }}</div>
            </div>
            <span class="pti-row-action" :class="'is-' + row.action">
              {{ row.action === 'reuse' ? 'Reuse existing' : row.action === 'create' ? 'Create copy' : 'Check on install' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('input', false)">Cancel</el-button>
      <el-button class="btn-6C30CC" :loading="installing" @click="confirm">
        Install template
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { dayLabel } from './_program-labels'

const TYPE_LABELS = {
  session: 'Session',
  topic: 'Town Hall topic',
  poll: 'Poll',
  checklist: 'Checklist',
  reel: 'Reel',
  custom_question: 'Custom question',
  spark: 'Spark set',
}

export default {
  name: 'template-install-modal',
  props: {
    value: { type: Boolean, default: false },
    template: { type: Object, default: null },
    preview: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    installing: { type: Boolean, default: false },
    previewError: { type: String, default: '' },
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
      },
    }
  },
  computed: {
    templateTitle() {
      return (this.template && this.template.title) || 'Program template'
    },
    rows() {
      if (this.preview && Array.isArray(this.preview.rows)) return this.preview.rows
      const items = (this.template && this.template.content && this.template.content.items) || []
      return items.map((item, index) => ({
        key: item.blueprintId || item.id || index,
        type: item.type,
        title: item.participantTitle || '',
        offsetDays: Number(item.offsetDays) || 0,
        action: this.previewError ? 'check' : 'create',
      }))
    },
  },
  watch: {
    template() {
      if (this.value) this.resetForm()
    },
  },
  methods: {
    dayLabel,
    typeLabel(type) {
      return TYPE_LABELS[type] || type || 'Item'
    },
    resetForm() {
      this.form = {
        title: (this.template && this.template.title) || '',
        description: (this.template && this.template.description) || '',
      }
    },
    confirm() {
      if (!this.template) return
      this.$emit('confirm', {
        templateId: this.template.id,
        title: this.form.title,
        description: this.form.description,
      })
    },
  },
}
</script>

<style>
.program-template-install-dialog .el-dialog__body {
  padding: 8px 24px 6px;
}
.program-template-install-dialog {
  max-width: calc(100vw - 32px);
}
</style>

<style scoped>
.pti {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.pti-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}
.pti-head-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}
.pti-close-btn {
  appearance: none;
  background: transparent;
  border: 1px solid #e2e4ea;
  color: #6b7280;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.pti-close-btn:hover {
  border-color: rgba(108, 48, 204, 0.30);
  color: #1a1a1a;
  background: #f2ecfb;
}
.pti-eyebrow,
.pti-label,
.pti-sequence-head {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}
.pti-head h2 {
  margin: 4px 0 0;
  font-size: 22px;
  line-height: 1.2;
  color: #1a1a1a;
}
.pti-counts {
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-shrink: 0;
}
.pti-counts span,
.pti-row-action {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: #f6f7f9;
  color: #6b7280;
  font-size: 11px;
  font-weight: 800;
}
.pti-counts strong { color: #1a1a1a; margin-right: 3px; }
.pti-form {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.pti-form label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pti-sequence {
  border: 1px solid #e2e4ea;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.pti-sequence-head {
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
  background: #fafbfc;
  border-bottom: 1px solid #eef0f5;
}
.pti-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px 14px;
  color: #6b7280;
  font-size: 13px;
}
.pti-preview-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #f1d48a;
  border-radius: 8px;
  background: #fffaf0;
  color: #7a5a00;
  font-size: 12px;
  line-height: 1.45;
}
.pti-preview-warning i { margin-top: 2px; }
.pti-row {
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
}
.pti-row:last-child { border-bottom: none; }
.pti-row-day {
  color: #6c30cc;
  font-size: 12px;
  font-weight: 800;
}
.pti-row-main { min-width: 0; }
.pti-row-title {
  color: #1a1a1a;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}
.pti-row-meta {
  margin-top: 2px;
  color: #8a8a8a;
  font-size: 11px;
  font-weight: 700;
}
.pti-row-action.is-create {
  background: #f2ecfb;
  color: #6c30cc;
}
.pti-row-action.is-reuse {
  background: #e8f7f1;
  color: #008966;
}
.pti-row-action.is-check {
  background: #fff7e1;
  color: #8a6500;
}
@media (max-width: 680px) {
  .pti-head { flex-direction: column; }
  .pti-row {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
}
</style>
